// pages/Membership.jsx
import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  useTheme,
  Paper,
} from '@mui/material';
import { FaStar, FaCheck, FaTicketAlt, FaPercent, FaCoffee, FaCouch, FaFilm, FaChevronDown } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const membershipTiers = [
  {
    name: 'Shooting Star',
    price: 'Free',
    color: '#4CAF50',
    icon: <FaStar />,
    description: 'The perfect way to enhance your movie experience with basic perks and special offers.',
    features: [
      { text: 'Online account for easy booking', icon: <FaTicketAlt /> },
      { text: 'Birthday ticket discount', icon: <FaPercent /> },
      { text: 'Email updates on new releases', icon: <FaFilm /> },
    ],
  },
  {
    name: 'Supernova',
    price: '$8/month or $75/year',
    color: '#2196F3',
    icon: <FaStar />,
    description: 'Step up your cinema experience with free tickets and discounts all year round.',
    features: [
      { text: 'One free ticket per month', icon: <FaTicketAlt /> },
      { text: '10% off concessions', icon: <FaPercent /> },
      { text: 'Free size upgrade on popcorn and drinks', icon: <FaCoffee /> },
      { text: 'No online booking fees', icon: <FaCheck /> },
    ],
  },
  {
    name: 'Galaxy',
    price: '$15/month or $150/year',
    color: '#9C27B0',
    icon: <FaStar />,
    recommended: true,
    description: 'Our premium tier with maximum benefits for the ultimate movie enthusiast.',
    features: [
      { text: 'Two free tickets per month', icon: <FaTicketAlt /> },
      { text: '15% off concessions', icon: <FaPercent /> },
      { text: 'Free refills on popcorn and drinks', icon: <FaCoffee /> },
      { text: 'Priority seating', icon: <FaCouch /> },
      { text: 'Exclusive preview screenings', icon: <FaFilm /> },
    ],
  },
];

const Membership = () => {
  const theme = useTheme();

  return (
    <Box>
      <Navbar />
      
      {/* Hero Section */}
      <Box 
        sx={{
          py: 12,
          background: 'linear-gradient(135deg, #0a1929 0%, #152238 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background star elements - similar to MembershipSpotlight */}
        {[...Array(20)].map((_, i) => (
          <Box
            key={i}
            component={FaStar}
            sx={{
              position: 'absolute',
              fontSize: Math.random() * 20 + 10,
              color: 'rgba(255, 255, 255, 0.15)',
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `twinkle ${Math.random() * 3 + 2}s infinite alternate`,
              '@keyframes twinkle': {
                '0%': { opacity: 0.1 },
                '100%': { opacity: 0.4 }
              }
            }}
          />
        ))}
        
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h1" 
              sx={{ 
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '3.75rem' },
                background: 'linear-gradient(45deg, #f5b400 30%, #f8d57e 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                textShadow: '0 2px 8px rgba(245,180,0,0.3)',
                animation: 'fadeInDown 1s ease-out',
                '@keyframes fadeInDown': {
                  '0%': { opacity: 0, transform: 'translateY(-20px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
              Stellar Membership
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'white',
                opacity: 0.9,
                maxWidth: '800px',
                mx: 'auto',
                mb: 4,
                animation: 'fadeIn 1s ease-out 0.3s both',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0 },
                  '100%': { opacity: 0.9 }
                }
              }}
            >
              Join our membership program and unlock a universe of exclusive benefits and cinematic experiences
            </Typography>
            
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                animation: 'fadeInUp 1s ease-out 0.6s both',
                '@keyframes fadeInUp': {
                  '0%': { opacity: 0, transform: 'translateY(20px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
              <Typography
                component={Box}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'white',
                  opacity: 0.7,
                  fontWeight: 500,
                  mt: 3,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'translateY(5px)',
                  }
                }}
                onClick={() => {
                  document.getElementById('membership-tiers').scrollIntoView({ 
                    behavior: 'smooth' 
                  });
                }}
              >
                View Membership Options
                <FaChevronDown size={16} style={{ marginLeft: '8px', animation: 'bounce 2s infinite' }} />
              </Typography>
            </Box>
            
            <style jsx global>{`
              @keyframes bounce {
                0%, 20%, 50%, 80%, 100% {
                  transform: translateY(0);
                }
                40% {
                  transform: translateY(-10px);
                }
                60% {
                  transform: translateY(-5px);
                }
              }
            `}</style>
          </Box>
        </Container>
      </Box>

      {/* Membership Benefits Overview */}
      <Box 
        sx={{ 
          py: 10, 
          bgcolor: 'background.default',
          position: 'relative',
          zIndex: 2,
          borderBottom: '1px solid rgba(255,255,255,0.05)'
        }}
      >
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            sx={{ 
              mb: 6, 
              fontWeight: 700,
              animation: 'fadeInDown 1s ease-out',
            }}
          >
            Benefits of Membership
          </Typography>
          
          <Grid container spacing={4}>
            {[
              { 
                title: 'Free Tickets', 
                icon: <FaTicketAlt size={40} color="#f5b400" />,
                description: 'Get regular free tickets automatically loaded to your account every month'
              },
              { 
                title: 'Special Discounts', 
                icon: <FaPercent size={40} color="#f5b400" />,
                description: 'Enjoy exclusive discounts on concessions, tickets, and special events'
              },
              { 
                title: 'Premium Experiences', 
                icon: <FaCouch size={40} color="#f5b400" />,
                description: 'Access exclusive screenings and priority booking for blockbuster premieres'
              },
            ].map((benefit, index) => (
              <Grid 
                item 
                xs={12} 
                md={4} 
                key={index}
                sx={{
                  animation: `fadeInUp ${0.3 + index * 0.2}s ease-out`,
                }}
              >
                <Paper 
                  elevation={2}
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: 4,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 12px 20px rgba(0,0,0,0.2)',
                    }
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {benefit.icon}
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body1">
                    {benefit.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Membership Tiers */}
      <Box 
        id="membership-tiers"
        sx={{
          py: 12,
          background: 'linear-gradient(to right, #0d1219, #1a1f2c)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            sx={{ 
              mb: 8, 
              fontWeight: 700,
              color: 'white',
              animation: 'fadeInDown 1s ease-out',
            }}
          >
            Choose Your Tier
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {membershipTiers.map((tier, index) => (
              <Paper
                key={tier.name}
                elevation={6}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  position: 'relative',
                  animation: `slideIn${index % 2 === 0 ? 'Left' : 'Right'} 0.8s ease-out`,
                  [`@keyframes slideIn${index % 2 === 0 ? 'Left' : 'Right'}`]: {
                    '0%': { 
                      opacity: 0, 
                      transform: `translateX(${index % 2 === 0 ? '-' : ''}100px)` 
                    },
                    '100%': { 
                      opacity: 1, 
                      transform: 'translateX(0)' 
                    }
                  },
                  transform: tier.recommended ? 'scale(1.05)' : 'scale(1)',
                  zIndex: tier.recommended ? 2 : 1,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: tier.recommended ? 'scale(1.08)' : 'scale(1.03)',
                  },
                  border: tier.recommended ? `3px solid ${tier.color}` : 'none',
                  boxShadow: tier.recommended 
                    ? `0 10px 30px ${tier.color}40` 
                    : '0 10px 30px rgba(0,0,0,0.2)',
                }}
              >
                {tier.recommended && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: -30,
                      transform: 'rotate(45deg)',
                      bgcolor: tier.color,
                      color: 'white',
                      py: 0.5,
                      px: 4,
                      width: 170,
                      textAlign: 'center',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      zIndex: 10,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  >
                    MOST POPULAR
                  </Box>
                )}
                
                <Grid container>
                  <Grid 
                    item 
                    xs={12} 
                    md={4}
                    sx={{ 
                      bgcolor: tier.color,
                      color: 'white',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      p: { xs: 4, md: 6 },
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    {[...Array(5)].map((_, i) => (
                      <Box
                        key={i}
                        component={FaStar}
                        sx={{
                          position: 'absolute',
                          fontSize: Math.random() * 15 + 8,
                          opacity: 0.2,
                          top: `${Math.random() * 100}%`,
                          left: `${Math.random() * 100}%`,
                        }}
                      />
                    ))}
                    
                    <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mb: 1, position: 'relative' }}>
                      {tier.name}
                    </Typography>
                    
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, position: 'relative' }}>
                      {tier.price}
                    </Typography>
                    
                    <Typography variant="body1" sx={{ opacity: 0.9, mb: 4, position: 'relative' }}>
                      {tier.description}
                    </Typography>
                    
                    <Button 
                      variant="contained" 
                      size="large"
                      sx={{ 
                        bgcolor: 'white',
                        color: tier.color,
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.9)',
                        },
                        fontWeight: 'bold',
                        py: 1.5,
                        position: 'relative',
                      }}
                    >
                      {tier.name === 'Shooting Star' ? 'Sign Up Free' : 'Subscribe Now'}
                    </Button>
                  </Grid>
                  
                  <Grid 
                    item 
                    xs={12} 
                    md={8}
                    sx={{ 
                      bgcolor: 'background.paper',
                      p: { xs: 4, md: 6 },
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                      Member Benefits:
                    </Typography>
                    
                    <List sx={{ mb: 2 }}>
                      {tier.features.map((feature, featureIndex) => (
                        <ListItem 
                          key={featureIndex}
                          sx={{ 
                            py: 1.5,
                            borderBottom: featureIndex !== tier.features.length - 1 ? 
                              '1px solid rgba(0,0,0,0.08)' : 'none',
                          }}
                        >
                          <ListItemIcon 
                            sx={{ 
                              color: tier.color,
                              minWidth: 40,
                            }}
                          >
                            {feature.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature.text} 
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    
                    <Typography variant="body2" sx={{ opacity: 0.7, mt: 4 }}>
                      {tier.name === 'Shooting Star' ? 
                        'No credit card required for this free tier' : 
                        'Cancel anytime. Subscription automatically renews until cancelled.'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: 'background.default' }}>
        <Container maxWidth="md">
          <Typography variant="h3" align="center" sx={{ mb: 6, fontWeight: 700 }}>
            Frequently Asked Questions
          </Typography>
          
          {[
            {
              question: 'How do I redeem my free tickets?',
              answer: 'Your free tickets are automatically added to your account on the 1st of each month. Simply log in and book your movie - the tickets will be applied at checkout.'
            },
            {
              question: 'Can I upgrade or downgrade my membership?',
              answer: 'Yes, you can change your membership tier at any time. The change will take effect on your next billing cycle.'
            },
            {
              question: 'What happens to unused tickets at the end of the month?',
              answer: 'Free tickets expire at the end of each month and do not roll over. Make sure to use them before they reset!'
            },
          ].map((faq, index) => (
            <Paper
              key={index}
              elevation={1}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                '&:hover': {
                  boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                },
                transition: 'box-shadow 0.3s ease',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {faq.question}
              </Typography>
              <Typography variant="body1">
                {faq.answer}
              </Typography>
            </Paper>
          ))}
        </Container>
      </Box>
      
      <Footer />
    </Box>
  );
};

export default Membership;